import { Injectable } from '@angular/core';

import { ApplicationStateService } from './application-state.service';
import { CommunicationService } from './communication.service';
import { Chapter } from '../models/chapter';
import { ChapterChangedEvent } from '../utils/events/chapter-changed-event';
import { CrudService } from './crud.service';
import { ChapterFactory } from '../models/factories/chapter-factory';

@Injectable({
    providedIn: 'root'
})
export class ChapterService {

    private chapters: Array<Chapter>;
    private chapterIdMap: Map<number, Chapter>;
    private chapterIndexMap: Map<number, Array<Chapter>>;

    constructor(private crudService: CrudService,
                private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService) {
    }

    setChapters(chapters: Array<Chapter>): void {
        this.chapters = [];
        this.chapterIdMap = undefined;
        this.chapterIndexMap = undefined;
        chapters.forEach(entity => this.chapters.push(entity as Chapter));
        this.initMaps();
    }

    /**
     * Similar to setChapters(chapters) but in addition the passed values are transformed in forehand.
     * This method purpose is to do the transformation and the storing in just one for-Loop.
     * @param objects
     */
    setObjectsToChapters(objects: Array<any>): void {
        this.chapters = [];
        this.chapterIdMap = undefined;
        this.chapterIndexMap = undefined;
        const chapterFactory = new ChapterFactory();
        objects.forEach(entity => {
            this.chapters.push(chapterFactory.buildFromObject(entity));
        });
        this.initMaps();
    }

    getFirstChapter(): Chapter | null {
        if (this.chapters) {
            return this.chapters.find(() => true);
        }

        return null;
    }

    getChapters(): Promise<Array<Chapter>> {
        return new Promise<Array<Chapter>>(resolve => {
            if (!this.applicationStateService.bookConfig.bookPermissions.bookChapter.view) {
                this.chapters = [];
            }

            if (this.chapters) {
                resolve(this.chapters);

                return;
            }

            return this.crudService.getAllEntitiesOfBook(Chapter, new ChapterFactory())
                .then(chapters => {
                    this.setChapters(chapters as Array<Chapter>);
                    resolve(chapters as Array<Chapter>);
                });
        });
    }

    getCurrentChapter(): Promise<Chapter> {

        const thiz = this;
        if (thiz.applicationStateService.currentChapter) {
            return new Promise((resolve => {
                resolve(thiz.applicationStateService.currentChapter);
            }));
        }

        return new Promise(((resolve, reject) => {
            resolve(undefined);
        }));
    }

    turnToChapter(chapter: Chapter): void {

        const chapterChangedEvent = new ChapterChangedEvent();
        this.applicationStateService.currentChapter = chapter;
        chapterChangedEvent.chapter = chapter;

        this.getNextChapter(chapter)
            .then(nextChapter => {
                chapterChangedEvent.nextChapter = nextChapter;
                this.getPreviousChapter()
                    .then(previousChapter => {
                        chapterChangedEvent.previousChapter = previousChapter;
                        this.communicationService.confirmChapterTurn(chapterChangedEvent);
                    });
            });
    }

    initMaps(): Promise<any> {

        const thiz = this;
        if (this.chapterIdMap && this.chapterIndexMap) {
            return new Promise(resolve =>
                resolve(null)
            );
        }

        const getSubChapterIndizes = chapter => {

            if (!thiz.chapterIndexMap.has(chapter.index)) {
                thiz.chapterIndexMap.set(chapter.index, [chapter]);
            } else {
                thiz.chapterIndexMap.get(chapter.index)
                    .push(chapter);
            }

            thiz.chapterIdMap.set(chapter.id, chapter);

            chapter.subChapters.forEach(subChapter => {
                getSubChapterIndizes(subChapter);
            });
        };

        thiz.chapterIndexMap = new Map();
        thiz.chapterIdMap = new Map();

        return new Promise(resolve => {
            thiz.getChapters()
                .then(result => {
                    result.forEach(chapter => {
                        getSubChapterIndizes(chapter);
                    });
                    resolve(null);
                });
        });
    }

    hasSubchapterSortingOrder(chapter: Chapter, sortingOrder: number): boolean {
        return chapter.subChapters[sortingOrder - 1] !== undefined;
    }

    getChapterFromIndex(index: number): Promise<Chapter> {

        return new Promise(resolve => {
            const keys = [];

            if (this.chapterIndexMap) {
                this.chapterIndexMap.forEach(((value, key) => {
                    keys.push(key);
                }));
            }

            keys.push(Number.MAX_VALUE);
            let lowerBound = 0;
            let chapter;

            for (let i = 0; i < keys.length - 1; i++) {

                if (keys[i] <= index && keys[i] >= lowerBound) {
                    const chapterPageArray = this.chapterIndexMap.get(keys[i]);
                    chapter = chapterPageArray[chapterPageArray.length - 1];
                    lowerBound = keys[i];
                }
            }
            resolve(chapter);
        });
    }

    getPreviousChapter(): Promise<Chapter> {

        return new Promise(resolve => {

            const currentChapterBeginning = this.applicationStateService.currentIndex;
            let prevChapterIndex = 0;
            let prevChapter;

            this.initMaps()
                .then(() => {
                    this.chapterIndexMap.forEach(((value, key) => {
                        if (key < currentChapterBeginning && key >= prevChapterIndex) {
                            const prevChapterArray = value;
                            prevChapter = prevChapterArray[prevChapterArray.length - 1];
                            prevChapterIndex = prevChapter.index;
                        }
                    }));

                    resolve(prevChapter);
                });
        });
    }

    getNextChapter(currentChapter?: Chapter): Promise<Chapter> {

        return new Promise(resolve => {

            if (!currentChapter) {
                currentChapter = this.applicationStateService.currentChapter;
            }

            if (!currentChapter) {
                currentChapter = new Chapter();
                currentChapter.index = 0;
            }

            let currentChapterBeginning = this.applicationStateService.currentIndex;
            const offset = (this.applicationStateService.currentViewMode.pagesToShow) -
                (currentChapterBeginning + 1) % this.applicationStateService.currentViewMode.pagesToShow;

            currentChapterBeginning += offset;

            let nextChapterIndex = Number.MAX_VALUE;
            let nextChapter;
            this.initMaps()
                .then(() => {
                    this.chapterIndexMap.forEach(((value, key) => {
                        if (key >= currentChapterBeginning && key <= nextChapterIndex) {
                            const nextChapterArray = value;
                            nextChapter = nextChapterArray[0];
                            nextChapterIndex = nextChapter.index;
                        }
                    }));
                    resolve(nextChapter);
                });
        });
    }

    getChapterHierarchyFromChapter(chapter: Chapter): Promise<Array<string | number>> {

        const thiz = this;
        const hierarchy = [];

        return new Promise(resolve => {
            thiz.initMaps()
                .then(() => {
                    while (chapter) {
                        hierarchy.unshift(thiz.getChapterNameToShow(chapter));
                        chapter = thiz.chapterIdMap.get(chapter.parentChapterId);
                    }
                    resolve(hierarchy);
                });
        });
    }

    getMainChapterStartAndEndIndex(chapter: Chapter): Promise<Array<number>> {

        return new Promise(resolve => {

            const currentChapterBeginning = chapter.index;
            let nextChapterIndex = Number.MAX_VALUE;
            let nextChapter;
            this.initMaps()
                .then(() => {
                    this.chapterIndexMap.forEach(((value, key) => {
                        if (key > currentChapterBeginning && key <= nextChapterIndex) {
                            const nextChapterArray = value;
                            // reduce to parent chapters only
                            const nextParentChapters = nextChapterArray.filter(ele => !ele.parentChapterId);
                            if (nextParentChapters.length >= 1) {
                                nextChapter = nextChapterArray[0];
                                nextChapterIndex = nextChapter.index;
                            }
                        }
                    }));
                    resolve([chapter.index, nextChapterIndex]);
                });
        });
    }

    /**
     * Determine what name the chapter has.
     *
     * @param {Chapter} chapter
     * @returns {string | number}
     */
    private getChapterNameToShow(chapter: Chapter): string | number {

        if (this.applicationStateService.bookConfig.chaptersOrderEnabled) {
            return chapter.sortOrder;
        } else if (chapter.orderName) {
            return chapter.orderName;
        }

        return chapter.sortOrder;
    }
}
